/**
 * Step 1 · Project Setup
 * Editable project meta — name, commodity, country, cutoff, density, currency.
 *
 * Sprint P14: expanded to 33 commodities via `/api/commodities` and shows
 * auto-detected density + cutoff (via `/api/projects/{pid}/defaults`)
 * with an "Apply auto-detected" button that pre-fills the fields.
 */
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { updateProject, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WizardCard, StatusRow } from "@/pages/wizard/_shared";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";


export default function ProjectSetupStep({ project, onDataChanged }) {
  const [form, setForm] = useState({
    name: project?.name || "",
    commodity: project?.commodity || "Au",
    country: project?.country || "",
    currency: project?.currency || "USD",
    description: project?.description || "",
    cutoff_grade: project?.cutoff_grade ?? "",
    default_density: project?.default_density ?? "",
  });
  useEffect(() => {
    setForm({
      name: project?.name || "",
      commodity: project?.commodity || "Au",
      country: project?.country || "",
      currency: project?.currency || "USD",
      description: project?.description || "",
      cutoff_grade: project?.cutoff_grade ?? "",
      default_density: project?.default_density ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fire only when the project identity changes; adding all sub-fields would clobber unsaved edits on every incoming stats refetch
  }, [project?.id]);

  // Full commodity registry — grouped by category for the dropdown.
  const commsQ = useQuery({
    queryKey: ["commodities-registry"],
    queryFn: () => api.get("/commodities").then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  // Auto-detected defaults for THIS project (density from assay column,
  // cutoff from commodity default). Refreshes on commodity change.
  const defaultsQ = useQuery({
    queryKey: ["project-defaults", project?.id, form.commodity],
    queryFn: () => api.get(`/projects/${project.id}/defaults`).then((r) => r.data),
    enabled: !!project?.id,
    staleTime: 30_000,
  });

  const qc = useQueryClient();
  const saveMut = useMutation({
    mutationFn: (body) => updateProject(project.id, body),
    onSuccess: () => {
      toast.success("Project saved");
      qc.invalidateQueries({ queryKey: ["wizard-project", project.id] });
      onDataChanged?.();
    },
    onError: (e) => toast.error(e?.message || "Save failed"),
  });

  const applyDetected = async () => {
    try {
      // Sprint P15 — explicit persistence via POST /apply-defaults.
      // The GET /defaults endpoint stays read-only for polling.
      const r = await api.post(`/projects/${project.id}/apply-defaults`);
      const d = r.data?.detected || defaultsQ.data;
      if (d) {
        setForm((s) => ({
          ...s,
          cutoff_grade:    d.cutoff,
          default_density: d.density,
        }));
        toast.success(`Applied cutoff ${d.cutoff} ${d.unit} · density ${d.density} t/m³`);
      }
      qc.invalidateQueries({ queryKey: ["wizard-project", project.id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Apply failed");
    }
  };

  const save = () => {
    const payload = {
      ...form,
      cutoff_grade:    form.cutoff_grade    === "" ? null : Number(form.cutoff_grade),
      default_density: form.default_density === "" ? null : Number(form.default_density),
    };
    saveMut.mutate(payload);
  };

  const gradeUnit = commsQ.data?.entries?.find((c) => c.code === form.commodity)?.unit || "g/t";

  return (
    <div className="max-w-3xl space-y-4">
      <StatusRow tone="info">
        Configure the project's identity — this powers every downstream
        step (commodity-appropriate cutoffs, country tax rates, reporting
        standards).
      </StatusRow>

      <WizardCard title="Identity" testId="w-project-setup-identity">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Project name</Label>
            <Input value={form.name}
                    data-testid="w-project-setup-name"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Country (ISO-2)</Label>
            <Input value={form.country} placeholder="AU / US / CA / ZA…"
                    maxLength={2}
                    data-testid="w-project-setup-country"
                    onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Commodity</Label>
            <Select value={form.commodity}
                     onValueChange={(v) => setForm({ ...form, commodity: v })}>
              <SelectTrigger data-testid="w-project-setup-commodity"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80">
                {commsQ.data?.grouped
                  ? Object.entries(commsQ.data.grouped).map(([cat, items]) => (
                      <div key={cat}>
                        <div className="px-2 pt-2 pb-1 text-[9px] font-mono uppercase tracking-widest text-slate-400">
                          {cat}
                        </div>
                        {items.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name} ({c.unit})
                          </SelectItem>
                        ))}
                      </div>
                    ))
                  : <SelectItem value="Au">Gold (g/t)</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Reporting currency</Label>
            <Input value={form.currency} maxLength={3}
                    data-testid="w-project-setup-currency"
                    onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
          </div>
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">Description</Label>
          <Input value={form.description}
                  data-testid="w-project-setup-description"
                  placeholder="Short project description for the CPR cover page"
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </WizardCard>

      <WizardCard title="Resource parameters (auto-detected · editable)"
                   testId="w-project-setup-resource">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">
              Cutoff grade ({gradeUnit})
            </Label>
            <Input type="number" step="0.01"
                    value={form.cutoff_grade}
                    placeholder={defaultsQ.data ? String(defaultsQ.data.cutoff) : "auto"}
                    data-testid="w-project-setup-cutoff"
                    onChange={(e) => setForm({ ...form, cutoff_grade: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">
              Default density (t/m³)
            </Label>
            <Input type="number" step="0.01"
                    value={form.default_density}
                    placeholder={defaultsQ.data ? String(defaultsQ.data.density) : "auto"}
                    data-testid="w-project-setup-density"
                    onChange={(e) => setForm({ ...form, default_density: e.target.value })} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button size="sm" variant="outline"
                    onClick={applyDetected} disabled={!defaultsQ.data}
                    data-testid="w-project-setup-apply-defaults">
            <Sparkle size={12} weight="fill" className="mr-1.5" />
            Apply auto-detected
          </Button>
          <div className="text-[10px] text-slate-500 mono">
            {defaultsQ.data
              ? `Density source: ${defaultsQ.data.density_source} · Cutoff source: ${defaultsQ.data.cutoff_source}`
              : "Auto-detection pending — upload data first"}
          </div>
        </div>
      </WizardCard>

      <div className="pt-2">
        <Button onClick={save}
                  disabled={saveMut.isPending
                               || !form.name.trim()
                               || !form.country
                               || form.country.length !== 2}
                  className="bg-brand-blue hover:bg-brand-blue/90 text-white"
                  data-testid="w-project-setup-save">
          {saveMut.isPending ? "Saving…" : "Save project"}
        </Button>
      </div>
    </div>
  );
}
